import { useQuery } from "@tanstack/react-query"
import { HeroVorgangName } from "@/components/portal/HeroVorgangName"
import { MailIdMarke } from "@/components/portal/MailIdMarke"
import { cleanSender, extractEmail, fmtDateLong } from "./format"

export type KartenSeite = "erledigen" | "verlauf" | "notizen" | "kunde"

/**
 * Kopf der Mail im neuen Postfach (Paul 28.10.2026).
 *
 * Wer schreibt, worum es geht, woran die Mail in HERO hängt — und darunter die
 * Reiter. Alle Aktionen stehen unten in der Fußleiste, nicht hier: der Kopf
 * soll in zwei Sekunden lesbar sein.
 *
 * 🔑 Die HERO-Zeile ist die Stelle, an der man die Zuordnung prüft. „ändern“
 * öffnet den Reiter „Kunde & HERO“ mit ALLEN bisherigen Möglichkeiten
 * (verknüpfen, Kontakt/Projekt anlegen, Ansprechpartner, Projekt wechseln …).
 */
export function KarteKopf({ e, emailId, seite, onSeite, notizAnzahl }: {
  e: {
    sender?: string | null; sender_email?: string | null; recipient?: string | null; cc?: string | null
    subject?: string | null; received_at?: string | null; category?: string | null
    email_direction?: string | null; postfach_label?: string | null; tenant_slug?: string | null
    hero_contact_id?: number | null; hero_contact_name?: string | null; hero_project_id?: number | null
  }
  emailId: number
  seite: KartenSeite
  onSeite: (s: KartenSeite) => void
  notizAnzahl: number
}) {
  // Der Name steht nicht immer an der Mail: wer per Plan verknüpft, schreibt
  // nur die ID (28.10.2026 auf dev gesehen: „Kontakt verknüpft“ ohne Namen).
  // Dann fragen wir HERO — derselbe Schlüssel wie Plan-Karte und Kunden-Reiter,
  // also kein zusätzlicher Abruf.
  const heroQ = useQuery<{ contact?: { first_name?: string; last_name?: string; company_name?: string } | null }>({
    queryKey: ["email-hero-projects", emailId],
    queryFn: async () => {
      const r = await fetch(`/api/v1/emails/${emailId}/hero-projects`, { credentials: "include" })
      if (!r.ok) return { projects: [] }
      return r.json()
    },
    enabled: !!e.hero_contact_id && !e.hero_contact_name,
    staleTime: 1000 * 30,
  })
  const k = heroQ.data?.contact
  const kontaktName = e.hero_contact_name
    || (k && ([k.first_name, k.last_name].filter(Boolean).join(" ") || k.company_name))
    || "verknüpft"
  const gesendet = e.email_direction === "sent"
  const wer = gesendet ? `An ${cleanSender(e.recipient) || "—"}` : cleanSender(e.sender) || e.sender_email || "—"
  const adresse = gesendet ? extractEmail(e.recipient) : extractEmail(e.sender)
  const initialen = (wer.replace(/^An /, "").match(/\b\p{L}/gu) || []).slice(0, 2).join("").toUpperCase() || "?"
  const reiter: [KartenSeite, string][] = [
    ["erledigen", "Erledigen"],
    ["verlauf", "Verlauf"],
    ["notizen", notizAnzahl > 0 ? `Notizen (${notizAnzahl})` : "Notizen"],
    ["kunde", "Kunde & HERO"],
  ]
  return (
    <div className="pk-kopf">
      <div className="pk-kopf-zeile">
        <span className="pk-avatar" aria-hidden>{initialen}</span>
        <div className="pk-kopf-wer">
          <span className="pk-kopf-name" data-pii>
            {wer}{adresse && <span className="pk-leise"> · {adresse}</span>}
          </span>
          <span className="pk-leise" data-pii>
            {[e.category, e.postfach_label, fmtDateLong(e.received_at)].filter(Boolean).join(" · ")}
          </span>
        </div>
        <MailIdMarke id={emailId} />
      </div>
      <h2 className="pk-betreff" data-pii>{e.subject || "(ohne Betreff)"}</h2>
      {(e.recipient || e.cc) && !gesendet && (
        <div className="pk-leise pk-verteiler" data-pii>
          {e.recipient && <span>An {e.recipient}</span>}
          {e.cc && <span><strong>Cc</strong> {e.cc}</span>}
        </div>
      )}
      <div className="pk-hero" data-testid="karte-hero">
        <span className="pk-hero-marke">HERO</span>
        {e.hero_contact_id ? (
          <span className="pk-hero-chip"><span className="pk-leise">Kontakt</span>
            <b data-pii>{kontaktName}</b></span>
        ) : (
          <span className="pk-hero-chip pk-hero-fehlt">Noch nicht zugeordnet</span>
        )}
        {e.hero_project_id ? (
          <span className="pk-hero-chip"><span className="pk-leise">Projekt</span>
            <b data-pii><HeroVorgangName tenantSlug={e.tenant_slug} projectId={e.hero_project_id} /></b></span>
        ) : null}
        <button type="button" className="pk-link" onClick={() => onSeite("kunde")}>
          {e.hero_contact_id ? "prüfen & ändern" : "zuordnen"}
        </button>
      </div>
      <div className="pk-reiter" role="tablist" aria-label="Zur Mail">
        {reiter.map(([k, l]) => (
          <button key={k} type="button" role="tab" aria-selected={seite === k}
            className={seite === k ? "aktiv" : undefined} onClick={() => onSeite(k)}>
            {l}
          </button>
        ))}
      </div>
    </div>
  )
}
